import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./ToolbarSelect.scss";

const ToolbarSelect = ({
  value,
  onChange,
  options,
  id,
  className = "",
  triggerLabel,
  "aria-label": ariaLabel = "Select option",
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected =
    options.find((option) => option.value === value) || options[0] || null;
  const labelText =
    triggerLabel != null && triggerLabel !== ""
      ? triggerLabel
      : selected
        ? selected.label
        : "Select";

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSelect = (nextValue) => {
    if (nextValue !== value) {
      onChange(nextValue);
    }
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={[
        "toolbar-select",
        open && "toolbar-select--open",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        id={id}
        className="toolbar-select__trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="toolbar-select__label">{labelText}</span>
        <span className="toolbar-select__chevron" aria-hidden="true" />
      </button>

      {open && (
        <ul className="toolbar-select__menu" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => {
            const isActive = option.value === value;
            return (
              <li
                key={option.value}
                className="toolbar-select__item"
                role="presentation"
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={[
                    "toolbar-select__option",
                    isActive && "toolbar-select__option--active",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleSelect(option.value)}
                >
                  <span
                    className="toolbar-select__check-slot"
                    aria-hidden="true"
                  >
                    {isActive && (
                      <i className="material-icons toolbar-select__check">
                        check
                      </i>
                    )}
                  </span>
                  <span className="toolbar-select__option-label">
                    {option.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

ToolbarSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  id: PropTypes.string,
  className: PropTypes.string,
  /** Overrides the label shown on the closed trigger (menu options unchanged). */
  triggerLabel: PropTypes.string,
  "aria-label": PropTypes.string,
};

export default ToolbarSelect;
